import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, AlertCircleIcon, ShieldCheckIcon, BeakerIcon, AwardIcon, ArrowLeftIcon, StarIcon, DownloadIcon, ShareIcon, BookmarkIcon, BarChart4Icon, ListChecksIcon, FlaskConicalIcon, FileTextIcon } from 'lucide-react';
export const ProductPage = () => {
  const {
    productId
  } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  useEffect(() => {
    // Simulate fetching product data
    const fetchProduct = () => {
      setLoading(true);
      // Mock data - in a real app, this would be fetched from an API
      const mockProducts = [{
        id: '1',
        name: 'BioBalance Pro Prebiotic',
        category: 'Prebiotic',
        company: 'NutriTech Labs',
        image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        healthClaim: 'Supports gut microbiome diversity and digestive health',
        certificationLevel: 3,
        transparencyScore: 92,
        purityResults: 'Passed all safety and purity tests. No contaminants detected.',
        bioavailabilityResults: 'High survivability through digestive simulation. Reaches target areas effectively.',
        efficacySummary: 'Claims fully supported by evidence',
        recommendation: 'Recommended',
        keyFindings: ['Contains all strains listed on label at specified quantities', 'Demonstrates measurable impact on beneficial gut bacteria in lab models', 'Clinical trial showed 42% improvement in microbiome diversity after 8 weeks', 'Statistically significant reduction in digestive discomfort reported by study participants'],
        ingredients: ['Inulin (from chicory root)', 'Fructooligosaccharides (FOS)', 'Galactooligosaccharides (GOS)', 'Resistant Starch', 'Acacia Fiber'],
        testingMethodology: ['Ingredient verification using chromatography', 'Contaminant screening for heavy metals and pesticides', 'In vitro fermentation models to measure prebiotic activity', 'Ex vivo human gut microbiome cultures', 'Randomized controlled trial with 120 participants'],
        clinicalResults: ['Increased abundance of Bifidobacteria by 64% compared to placebo', 'Increased short-chain fatty acid production by 38%', 'Reduced digestive discomfort scores by 47% in IBS-C patients', 'Improved stool consistency in 76% of participants', 'No significant adverse effects reported'],
        certificationDate: '2023-09-15',
        certificationExpiry: '2025-09-15',
        manufacturingLocation: 'San Diego, California, USA',
        dosageInstructions: 'Mix 1 scoop (5g) with water or beverage of choice once daily, preferably with a meal.',
        safetyNotes: 'Generally recognized as safe. May cause mild gas or bloating during first week of use as microbiome adapts.'
      }, {
        id: '2',
        name: 'ImmunoStrength Plus',
        category: 'Immune Support',
        company: 'VitalCore Supplements',
        image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        healthClaim: 'Boosts immune function and reduces inflammation',
        certificationLevel: 2,
        transparencyScore: 78,
        purityResults: 'Passed safety tests. Minor variance in vitamin C content (12% below label claim).',
        bioavailabilityResults: 'Moderate absorption rate in digestive simulation.',
        efficacySummary: 'Claims partially supported by evidence',
        recommendation: 'Conditionally Recommended',
        keyFindings: ['Contains key ingredients but with some variance from label claims', 'Shows moderate anti-inflammatory activity in cell models', 'Increases NK cell activity in lab testing', 'No human clinical trials completed yet to verify immune enhancement claims'],
        ingredients: ['Vitamin C (as ascorbic acid)', 'Vitamin D3 (cholecalciferol)', 'Zinc (as zinc gluconate)', 'Elderberry extract', 'Echinacea purpurea extract', 'Quercetin'],
        testingMethodology: ['HPLC analysis of vitamin content', 'ICP-MS testing for mineral content and heavy metals', 'In vitro inflammatory marker testing using human cell lines', 'NK cell activation assay', 'Antioxidant capacity measurement'],
        clinicalResults: ['Reduced inflammatory cytokine production by 32% in cell models', 'Increased NK cell activity by 47% in ex vivo testing', 'Demonstrated antioxidant activity equivalent to 200mg vitamin C', 'Preliminary pilot study (n=30) showed reduced duration of cold symptoms'],
        certificationDate: '2023-05-22',
        certificationExpiry: '2025-05-22',
        manufacturingLocation: 'Portland, Oregon, USA',
        dosageInstructions: 'Take 2 capsules daily with food.',
        safetyNotes: 'Not recommended for those taking immunosuppressant medications. Consult healthcare provider if pregnant or nursing.'
      }, {
        id: '3',
        name: 'GutGuard Digestive Enzymes',
        category: 'Digestive Support',
        company: 'BioCare Health',
        image: 'https://images.unsplash.com/photo-1550572017-edd951b55104?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        healthClaim: 'Improves nutrient absorption and reduces digestive discomfort',
        certificationLevel: 1,
        transparencyScore: 85,
        purityResults: 'Passed all safety tests. Enzyme activity matches label claims.',
        bioavailabilityResults: 'Not tested at Level 1 certification.',
        efficacySummary: 'Safety and content verified, efficacy not validated',
        recommendation: 'Verified for Safety',
        keyFindings: ['Contains all enzymes listed at specified activity levels', 'Free from harmful contaminants and allergens', 'Manufacturing follows cGMP standards', 'No efficacy testing completed to verify digestive improvement claims'],
        ingredients: ['Amylase', 'Protease', 'Lipase', 'Lactase', 'Cellulase', 'Bromelain', 'Papain'],
        testingMethodology: ['Enzyme activity assays for each component', 'Microbial contamination screening', 'Allergen testing', 'Heavy metal screening', 'Manufacturing facility audit'],
        clinicalResults: ['No clinical efficacy testing performed at Level 1 certification', 'Enzyme activity confirmed in simulated gastric conditions', 'Meets USP standards for enzyme potency'],
        certificationDate: '2023-11-08',
        certificationExpiry: '2024-11-08',
        manufacturingLocation: 'Chicago, Illinois, USA',
        dosageInstructions: 'Take 1 capsule at the beginning of each meal. May increase to 2 capsules for larger meals.',
        safetyNotes: 'Generally well-tolerated. Contains trace amounts of milk proteins from manufacturing process.'
      }, {
        id: '4',
        name: 'MicrobiomePlus Daily',
        category: 'Probiotic',
        company: 'Vitality Sciences',
        image: 'https://images.unsplash.com/photo-1576671414121-aa0c81c869e3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        healthClaim: 'Restores microbiome balance and improves gut health',
        certificationLevel: 2,
        transparencyScore: 88,
        purityResults: 'Passed safety tests. CFU count 15% lower than label claim.',
        bioavailabilityResults: 'Good survivability in acid conditions. Colonization demonstrated in lab models.',
        efficacySummary: 'Claims partially supported by evidence',
        recommendation: 'Recommended with Notes',
        keyFindings: ['Contains all probiotic strains listed but at lower concentrations than claimed', 'Demonstrates ability to temporarily colonize the gut in lab models', 'Shows positive modulation of gut microbiome composition', 'Further human studies needed to validate long-term health benefits'],
        ingredients: ['Lactobacillus acidophilus', 'Bifidobacterium lactis', 'Lactobacillus plantarum', 'Lactobacillus rhamnosus', 'Bifidobacterium longum', 'Inulin (prebiotic)'],
        testingMethodology: ['Strain identification using 16S rRNA sequencing', 'Colony-forming unit (CFU) count verification', 'Acid and bile salt resistance testing', 'Adhesion assays to intestinal cell lines', 'Simulated gut model for colonization assessment'],
        clinicalResults: ['Survived simulated gastric passage with 68% viability', 'Demonstrated adhesion to intestinal cells in vitro', 'Pilot study (n=45) showed increased beneficial bacteria after 4 weeks', 'Reduced bloating reported by 62% of pilot study participants'],
        certificationDate: '2023-08-03',
        certificationExpiry: '2025-08-03',
        manufacturingLocation: 'Boston, Massachusetts, USA',
        dosageInstructions: 'Take 1 capsule daily with a meal. Store in refrigerator for maximum potency.',
        safetyNotes: 'Safe for most individuals. May cause temporary gas or bloating when first starting. Contains trace milk proteins.'
      }];
      const foundProduct = mockProducts.find(p => p.id === productId);
      setTimeout(() => {
        setProduct(foundProduct || null);
        setLoading(false);
      }, 800); // Simulate network delay
    };
    fetchProduct();
  }, [productId]);
  const getCertificationBadge = level => {
    switch (level) {
      case 1:
        return <div className="flex items-center" data-id="element-1103">
            <ShieldCheckIcon size={20} className="text-blue-600 mr-1" data-id="element-1104" />
            <span className="text-sm font-medium" data-id="element-1105">
              Level 1: Evidence Screen
            </span>
          </div>;
      case 2:
        return <div className="flex items-center" data-id="element-1106">
            <BeakerIcon size={20} className="text-blue-600 mr-1" data-id="element-1107" />
            <span className="text-sm font-medium" data-id="element-1108">
              Level 2: Mechanistic Validation
            </span>
          </div>;
      case 3:
        return <div className="flex items-center" data-id="element-1109">
            <AwardIcon size={20} className="text-blue-600 mr-1" data-id="element-1110" />
            <span className="text-sm font-medium" data-id="element-1111">
              Level 3: Clinical Validation
            </span>
          </div>;
      default:
        return null;
    }
  };
  const getRecommendationBadge = recommendation => {
    switch (recommendation) {
      case 'Recommended':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1112">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-1113" />
            Recommended
          </span>;
      case 'Conditionally Recommended':
      case 'Recommended with Notes':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1114">
            <AlertCircleIcon size={12} className="mr-1" data-id="element-1115" />
            {recommendation}
          </span>;
      case 'Verified for Safety':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1116">
            <ShieldCheckIcon size={12} className="mr-1" data-id="element-1117" />
            Verified for Safety
          </span>;
      case 'Not Recommended':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1118">
            <XCircleIcon size={12} className="mr-1" data-id="element-1119" />
            Not Recommended
          </span>;
      default:
        return null;
    }
  };
  if (loading) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-1120">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1121"></div>
        <p className="mt-4 text-gray-600" data-id="element-1122">Loading product information...</p>
      </div>;
  }
  if (!product) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-1123">
        <div className="text-center" data-id="element-1124">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1125">
            Product Not Found
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-1126">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/product-reviews" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1127">
            <ArrowLeftIcon size={16} className="mr-2" data-id="element-1128" />
            Back to Product Reviews
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-white w-full" data-id="element-1129">
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-gray-50" data-id="element-1130">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1131">
          <div className="mb-6" data-id="element-1132">
            <Link to="/product-reviews" className="inline-flex items-center text-blue-600 hover:text-blue-800" data-id="element-1133">
              <ArrowLeftIcon size={16} className="mr-1" data-id="element-1134" />
              Back to Product Reviews
            </Link>
          </div>
          <div className="bg-white rounded-xl overflow-hidden shadow-md" data-id="element-1135">
            <div className="md:flex" data-id="element-1136">
              <div className="md:w-1/3 h-64 md:h-auto" data-id="element-1137">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-1138" />
              </div>
              <div className="p-8 md:w-2/3" data-id="element-1139">
                <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-1140">
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide" data-id="element-1141">
                    {product.category}
                  </span>
                  {getRecommendationBadge(product.recommendation)}
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-1142">
                  {product.name}
                </h1>
                <p className="text-gray-600 mb-4" data-id="element-1143">
                  By <span className="font-medium" data-id="element-1144">{product.company}</span>
                </p>
                <div className="mb-6" data-id="element-1145">
                  {getCertificationBadge(product.certificationLevel)}
                </div>
                <div className="mb-6" data-id="element-1146">
                  <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1147">
                    Health Claim
                  </h2>
                  <p className="text-gray-700 italic" data-id="element-1148">
                    "{product.healthClaim}"
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 mb-6" data-id="element-1149">
                  <div data-id="element-1150">
                    <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1151">
                      Transparency Score
                    </h2>
                    <div className="flex items-center" data-id="element-1152">
                      <div className="w-32 bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-1153">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{
                        width: `${product.transparencyScore}%`
                      }} data-id="element-1154"></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700" data-id="element-1155">
                        {product.transparencyScore}%
                      </span>
                    </div>
                  </div>
                  <div data-id="element-1156">
                    <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1157">
                      Certification Date
                    </h2>
                    <p className="text-gray-700" data-id="element-1158">{product.certificationDate}</p>
                  </div>
                  <div data-id="element-1159">
                    <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1160">
                      Valid Until
                    </h2>
                    <p className="text-gray-700" data-id="element-1161">
                      {product.certificationExpiry}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3" data-id="element-1162">
                  <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1163">
                    <DownloadIcon size={16} className="mr-2" data-id="element-1164" />
                    Download Report
                  </button>
                  <button className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors" data-id="element-1165">
                    <ShareIcon size={16} className="mr-2" data-id="element-1166" />
                    Share
                  </button>
                  <button className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors" data-id="element-1167">
                    <BookmarkIcon size={16} className="mr-2" data-id="element-1168" />
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Tabs Section */}
      <section className="py-8 bg-white border-b" data-id="element-1169">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1170">
          <div className="flex overflow-x-auto scrollbar-hide space-x-8" data-id="element-1171">
            <button className={`pb-4 px-1 font-medium text-sm flex items-center whitespace-nowrap ${activeTab === 'overview' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('overview')} data-id="element-1172">
              <FileTextIcon size={16} className="mr-2" data-id="element-1173" />
              Overview
            </button>
            <button className={`pb-4 px-1 font-medium text-sm flex items-center whitespace-nowrap ${activeTab === 'testing' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('testing')} data-id="element-1174">
              <BeakerIcon size={16} className="mr-2" data-id="element-1175" />
              Testing Methodology
            </button>
            <button className={`pb-4 px-1 font-medium text-sm flex items-center whitespace-nowrap ${activeTab === 'results' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('results')} data-id="element-1176">
              <BarChart4Icon size={16} className="mr-2" data-id="element-1177" />
              Results & Findings
            </button>
            <button className={`pb-4 px-1 font-medium text-sm flex items-center whitespace-nowrap ${activeTab === 'usage' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('usage')} data-id="element-1178">
              <ListChecksIcon size={16} className="mr-2" data-id="element-1179" />
              Usage & Safety
            </button>
          </div>
        </div>
      </section>
      {/* Content Section */}
      <section className="py-12 bg-gray-50" data-id="element-1180">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1181">
          {activeTab === 'overview' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white p-8 rounded-lg shadow-sm" data-id="element-1182">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1183">
                Product Overview
              </h2>
              <div className="mb-8" data-id="element-1184">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1185">
                  Key Findings
                </h3>
                <ul className="space-y-3" data-id="element-1186">
                  {product.keyFindings.map((finding, index) => <li key={index} className="flex items-start" data-id="element-1187">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1188" />
                      <span className="text-gray-700" data-id="element-1189">{finding}</span>
                    </li>)}
                </ul>
              </div>
              <div className="mb-8" data-id="element-1190">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1191">
                  Ingredients
                </h3>
                <div className="bg-gray-50 p-6 rounded-md" data-id="element-1192">
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1193">
                    {product.ingredients.map((ingredient, index) => <li key={index} className="flex items-center" data-id="element-1194">
                        <div className="h-2 w-2 bg-blue-600 rounded-full mr-3" data-id="element-1195"></div>
                        <span className="text-gray-700" data-id="element-1196">{ingredient}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1197">
                <div className="bg-blue-50 p-6 rounded-lg" data-id="element-1198">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1199">
                    Purity Results
                  </h3>
                  <p className="text-gray-700" data-id="element-1200">{product.purityResults}</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-lg" data-id="element-1201">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1202">
                    Bioavailability Results
                  </h3>
                  <p className="text-gray-700" data-id="element-1203">
                    {product.bioavailabilityResults}
                  </p>
                </div>
                <div className="bg-blue-50 p-6 rounded-lg" data-id="element-1204">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1205">
                    Efficacy Summary
                  </h3>
                  <p className="text-gray-700" data-id="element-1206">{product.efficacySummary}</p>
                </div>
              </div>
            </motion.div>}
          {activeTab === 'testing' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white p-8 rounded-lg shadow-sm" data-id="element-1207">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1208">
                Testing Methodology
              </h2>
              <div className="mb-8" data-id="element-1209">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1210">
                  Certification Level Explained
                </h3>
                <div className="bg-gray-50 p-6 rounded-md mb-6" data-id="element-1211">
                  {product.certificationLevel === 1 && <div className="flex items-start" data-id="element-1212">
                      <ShieldCheckIcon size={24} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1213" />
                      <div data-id="element-1214">
                        <h4 className="font-medium text-gray-900 mb-2" data-id="element-1215">
                          Level 1: Evidence Screen
                        </h4>
                        <p className="text-gray-700" data-id="element-1216">
                          This product has undergone basic safety testing and
                          ingredient verification. We've confirmed what's in the
                          product matches the label and that it's free from
                          harmful contaminants. However, at Level 1, we have not
                          verified the product's efficacy claims through
                          mechanistic or clinical testing.
                        </p>
                      </div>
                    </div>}
                  {product.certificationLevel === 2 && <div className="flex items-start" data-id="element-1217">
                      <BeakerIcon size={24} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1218" />
                      <div data-id="element-1219">
                        <h4 className="font-medium text-gray-900 mb-2" data-id="element-1220">
                          Level 2: Mechanistic Validation
                        </h4>
                        <p className="text-gray-700" data-id="element-1221">
                          This product has passed safety testing and ingredient
                          verification. Additionally, it has demonstrated
                          biological activity in laboratory models that supports
                          its mechanism of action. This means there is
                          scientific evidence showing how the product works in
                          controlled settings, but full clinical validation in
                          humans may still be pending.
                        </p>
                      </div>
                    </div>}
                  {product.certificationLevel === 3 && <div className="flex items-start" data-id="element-1222">
                      <AwardIcon size={24} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1223" />
                      <div data-id="element-1224">
                        <h4 className="font-medium text-gray-900 mb-2" data-id="element-1225">
                          Level 3: Clinical Validation
                        </h4>
                        <p className="text-gray-700" data-id="element-1226">
                          This product has achieved our highest level of
                          certification. It has passed safety testing,
                          demonstrated its mechanism of action in laboratory
                          models, and shown efficacy in human clinical trials.
                          The health claims made by the manufacturer are
                          supported by robust scientific evidence including
                          controlled human studies.
                        </p>
                      </div>
                    </div>}
                </div>
              </div>
              <div className="mb-8" data-id="element-1227">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1228">
                  Testing Procedures
                </h3>
                <ul className="space-y-4" data-id="element-1229">
                  {product.testingMethodology.map((method, index) => <li key={index} className="flex items-start" data-id="element-1230">
                      <FlaskConicalIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1231" />
                      <span className="text-gray-700" data-id="element-1232">{method}</span>
                    </li>)}
                </ul>
              </div>
              <div data-id="element-1233">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1234">
                  Manufacturing Information
                </h3>
                <div className="bg-gray-50 p-6 rounded-md" data-id="element-1235">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1236">
                    <div data-id="element-1237">
                      <h4 className="font-medium text-gray-900 mb-2" data-id="element-1238">
                        Manufacturing Location
                      </h4>
                      <p className="text-gray-700" data-id="element-1239">
                        {product.manufacturingLocation}
                      </p>
                    </div>
                    <div data-id="element-1240">
                      <h4 className="font-medium text-gray-900 mb-2" data-id="element-1241">
                        Certification Valid Until
                      </h4>
                      <p className="text-gray-700" data-id="element-1242">
                        {product.certificationExpiry}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>}
          {activeTab === 'results' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white p-8 rounded-lg shadow-sm" data-id="element-1243">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1244">
                Results & Findings
              </h2>
              <div className="mb-8" data-id="element-1245">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1246">
                  Clinical Results
                </h3>
                {product.clinicalResults && product.clinicalResults.length > 0 ? <ul className="space-y-3" data-id="element-1247">
                    {product.clinicalResults.map((result, index) => <li key={index} className="flex items-start" data-id="element-1248">
                        <BarChart4Icon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1249" />
                        <span className="text-gray-700" data-id="element-1250">{result}</span>
                      </li>)}
                  </ul> : <p className="text-gray-700 italic" data-id="element-1251">
                    No clinical results available at this certification level.
                  </p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1252">
                <div data-id="element-1253">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1254">
                    Efficacy Assessment
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-md" data-id="element-1255">
                    <div className="flex items-center mb-4" data-id="element-1256">
                      {product.recommendation === 'Recommended' && <CheckCircleIcon size={24} className="text-green-600 mr-3" data-id="element-1257" />}
                      {(product.recommendation === 'Conditionally Recommended' || product.recommendation === 'Recommended with Notes') && <AlertCircleIcon size={24} className="text-yellow-600 mr-3" data-id="element-1258" />}
                      {product.recommendation === 'Verified for Safety' && <ShieldCheckIcon size={24} className="text-blue-600 mr-3" data-id="element-1259" />}
                      {product.recommendation === 'Not Recommended' && <XCircleIcon size={24} className="text-red-600 mr-3" data-id="element-1260" />}
                      <h4 className="font-medium text-gray-900" data-id="element-1261">
                        {product.recommendation}
                      </h4>
                    </div>
                    <p className="text-gray-700" data-id="element-1262">{product.efficacySummary}</p>
                  </div>
                </div>
                <div data-id="element-1263">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1264">
                    Transparency Assessment
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-md" data-id="element-1265">
                    <div className="mb-4" data-id="element-1266">
                      <div className="flex justify-between mb-1" data-id="element-1267">
                        <span className="text-sm font-medium text-gray-700" data-id="element-1268">
                          Transparency Score
                        </span>
                        <span className="text-sm font-medium text-gray-700" data-id="element-1269">
                          {product.transparencyScore}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1270">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{
                      width: `${product.transparencyScore}%`
                    }} data-id="element-1271"></div>
                      </div>
                    </div>
                    <p className="text-gray-700" data-id="element-1272">
                      {product.transparencyScore >= 90 ? 'Excellent transparency. The manufacturer provided complete access to data, formulations, and manufacturing processes.' : product.transparencyScore >= 75 ? 'Good transparency. The manufacturer shared most key information but may have withheld some proprietary details.' : product.transparencyScore >= 60 ? 'Moderate transparency. The manufacturer provided basic information but limited access to complete data or methodology.' : 'Limited transparency. The manufacturer was reluctant to share important information about the product or its testing.'}
                    </p>
                  </div>
                </div>
              </div>
              <div data-id="element-1273">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1274">
                  Health Claim Assessment
                </h3>
                <div className="bg-blue-50 p-6 rounded-md" data-id="element-1275">
                  <p className="text-gray-700 italic mb-4" data-id="element-1276">
                    "{product.healthClaim}"
                  </p>
                  <p className="text-gray-700" data-id="element-1277">
                    {product.recommendation === 'Recommended' ? 'Our testing fully supports this health claim based on comprehensive evidence including clinical trials.' : product.recommendation === 'Conditionally Recommended' || product.recommendation === 'Recommended with Notes' ? 'Our testing partially supports this health claim. There is some evidence for the claimed benefits, but additional research would strengthen the claim.' : product.recommendation === 'Verified for Safety' ? "While we've verified this product is safe and contains the listed ingredients, we have not validated the efficacy of this health claim." : 'Our testing does not support this health claim based on available evidence.'}
                  </p>
                </div>
              </div>
            </motion.div>}
          {activeTab === 'usage' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white p-8 rounded-lg shadow-sm" data-id="element-1278">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1279">
                Usage & Safety Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1280">
                <div data-id="element-1281">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1282">
                    Recommended Usage
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-md" data-id="element-1283">
                    <p className="text-gray-700" data-id="element-1284">
                      {product.dosageInstructions}
                    </p>
                  </div>
                </div>
                <div data-id="element-1285">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1286">
                    Safety Notes
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-md" data-id="element-1287">
                    <p className="text-gray-700" data-id="element-1288">{product.safetyNotes}</p>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-1289">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1290">
                  Ingredients
                </h3>
                <div className="bg-gray-50 p-6 rounded-md" data-id="element-1291">
                  <ul className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-1292">
                    {product.ingredients.map((ingredient, index) => <li key={index} className="flex items-center" data-id="element-1293">
                        <div className="h-2 w-2 bg-blue-600 rounded-full mr-3" data-id="element-1294"></div>
                        <span className="text-gray-700" data-id="element-1295">{ingredient}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              <div data-id="element-1296">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1297">
                  Important Disclaimers
                </h3>
                <div className="bg-blue-50 p-6 rounded-md" data-id="element-1298">
                  <ul className="space-y-3" data-id="element-1299">
                    <li className="flex items-start" data-id="element-1300">
                      <AlertCircleIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1301" />
                      <p className="text-gray-700" data-id="element-1302">
                        Ukweli certification does not replace medical advice.
                        Consult with a healthcare professional before starting
                        any supplement.
                      </p>
                    </li>
                    <li className="flex items-start" data-id="element-1303">
                      <AlertCircleIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1304" />
                      <p className="text-gray-700" data-id="element-1305">
                        Individual results may vary. Our testing evaluates
                        general efficacy, but responses can differ based on
                        individual factors.
                      </p>
                    </li>
                    <li className="flex items-start" data-id="element-1306">
                      <AlertCircleIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1307" />
                      <p className="text-gray-700" data-id="element-1308">
                        This product has not been evaluated by the FDA. It is
                        not intended to diagnose, treat, cure, or prevent any
                        disease.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>}
        </div>
      </section>
      {/* Related Products Section */}
      <section className="py-16 bg-white" data-id="element-1309">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1310">
          <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1311">
            Similar Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1312">
            {[1, 2, 3].map(id => {
            if (id.toString() === productId) return null;
            return <Link key={id} to={`/product/${id}`} className="bg-gray-50 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-1313">
                  <div className="h-48 overflow-hidden" data-id="element-1314">
                    <img src={`https://images.unsplash.com/photo-${id === 1 ? '1584308666744-24d5c474f2ae' : id === 2 ? '1587854692152-cbe660dbde88' : '1550572017-edd951b55104'}?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80`} alt="Product" className="w-full h-full object-cover" data-id="element-1315" />
                  </div>
                  <div className="p-4" data-id="element-1316">
                    <div className="flex justify-between items-start mb-2" data-id="element-1317">
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide" data-id="element-1318">
                        {id === 1 ? 'Prebiotic' : id === 2 ? 'Immune Support' : 'Digestive Support'}
                      </span>
                      {id === 1 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1319">
                          <CheckCircleIcon size={10} className="mr-1" data-id="element-1320" />
                          Recommended
                        </span> : id === 2 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1321">
                          <AlertCircleIcon size={10} className="mr-1" data-id="element-1322" />
                          Conditional
                        </span> : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1323">
                          <ShieldCheckIcon size={10} className="mr-1" data-id="element-1324" />
                          Verified
                        </span>}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-1325">
                      {id === 1 ? 'BioBalance Pro Prebiotic' : id === 2 ? 'ImmunoStrength Plus' : 'GutGuard Digestive Enzymes'}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3" data-id="element-1326">
                      By{' '}
                      {id === 1 ? 'NutriTech Labs' : id === 2 ? 'VitalCore Supplements' : 'BioCare Health'}
                    </p>
                    <div className="flex items-center" data-id="element-1327">
                      {id === 1 ? <AwardIcon size={16} className="text-blue-600 mr-1" data-id="element-1328" /> : id === 2 ? <BeakerIcon size={16} className="text-blue-600 mr-1" data-id="element-1329" /> : <ShieldCheckIcon size={16} className="text-blue-600 mr-1" data-id="element-1330" />}
                      <span className="text-xs font-medium text-gray-700" data-id="element-1331">
                        Level {id === 1 ? '3' : id === 2 ? '2' : '1'}{' '}
                        Certification
                      </span>
                    </div>
                  </div>
                </Link>;
          })}
          </div>
        </div>
      </section>
    </div>;
};